<template>
  <div class="p-room-form__file">
    <label class="c-file">
      <input
        type="file"
        accept="image/*,
        .pdf,
        .docx,
        .doc,
        .xlsx,
        .xls,
        .pptx,
        .ppt"
        class="c-file__input"
        multiple
        ref="file"
        @change="changeFile"
      />
      <i class="fas fa-images fa-lg"></i>
    </label>
    <Modal
      v-if="isOpenModal"
      appear
      notCloseBg
      class="p-room-form-modal"
      @close="closeModal"
    >
      <template slot="title">
        ファイルの送信（{{ current + 1 }}/{{ total }}）
      </template>
      <template slot="content">
        <div class="c-image--thumbnail" v-if="currentFileData.thumbnail">
          <img :src="currentFileData.thumbnail" alt="" />
        </div>
        <div class="c-fieldset">
          <div class="c-fieldset__header">
            <p class="c-fieldset__title">ファイル名</p>
          </div>
          <div class="c-fieldset__content">
            <div class="c-fileName">
              <Input v-model="fileName" @focusout="focusoutInput" />
              <div class="c-fileName__extension">
                {{ currentFileData.extension }}
              </div>
            </div>
          </div>
        </div>
        <div class="c-fieldset">
          <div class="c-fieldset__header">
            <p class="c-fieldset__title">メッセージ</p>
          </div>
          <div class="c-fieldset__content">
            <Textarea v-model="message" autosize :rows="1" ref="textarea" />
          </div>
        </div>
      </template>
      <template slot="footer">
        <transition name="v-fade">
          <Alert v-if="error" size="sm">{{ error }}</Alert>
        </transition>
        <div class="c-btns">
          <Button
            variant="primary"
            :class="{ 'is-loading': isProcessing }"
            @click="submit"
          >
            送信
          </Button>
          <Button variant="secondary" @click="closeModal">
            キャンセル
          </Button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import {
  ALLOW_TYPES,
  ERROR_MESSAGE,
  ERROR_MESSAGE_SHOW_DURATION,
  FILE_SIZE_LIMIT_IMG,
  FILE_SIZE_LIMIT_FILE,
  THUMBNAIL_MAX_HEIGHT,
  THUMBNAIL_MAX_WIDTH
} from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Input from '@/components/Input';
import Modal from '@/components/Modal';
import Textarea from '@/components/Textarea';
import utils from '@/utils';

export default {
  components: {
    Alert,
    Button,
    Input,
    Modal,
    Textarea
  },
  data() {
    return {
      current: null,
      error: '',
      fileName: '',
      filesData: [],
      message: '',
      timeoutId: null,
      isOpenModal: false,
      isProcessing: false
    };
  },
  computed: {
    currentFileData() {
      return this.filesData[this.current];
    },
    room() {
      return this.$store.getters.currentRoom;
    },
    total() {
      return this.filesData.length;
    },
    isReadOnlyMode() {
      return this.$store.state.isReadOnlyMode;
    }
  },
  methods: {
    changeFile(e) {
      if (this.isReadOnlyMode) {
        this.$refs.file.value = '';
        return;
      }
      this.readFiles(e.target.files);
      // 空にしておかないと同じファイルをあげた時などにchangeイベントが発火しない
      this.$refs.file.value = '';
    },
    checkFile(file) {
      let error = [];
      if (!ALLOW_TYPES.includes(file.type)) {
        error.push(ERROR_MESSAGE.imageType);
      }
      if (utils.isImageType(file.type)) {
        if (file.size > FILE_SIZE_LIMIT_IMG) {
          error.push(ERROR_MESSAGE.imageSize);
        }
      } else {
        if (file.size > FILE_SIZE_LIMIT_FILE) {
          error.push(ERROR_MESSAGE.fileSize);
        }
      }
      return error;
    },
    closeModal() {
      if (this.isProcessing) {
        return;
      }
      this.isOpenModal = false;
      this.current++;
      if (this.current < this.total) {
        this.openModal();
      }
    },
    focusoutInput() {
      // ファイル名の値が空の場合は、元のファイル名を入れる
      const value = this.fileName.trim();
      if (!value && value !== 0) {
        this.fileName = this.currentFileData.name;
      }
    },
    focusTextarea() {
      this.$refs.textarea.$el.focus();
    },
    getExtension(name) {
      let extension = name.split('.').pop();
      if (extension) {
        extension = '.' + extension;
      }
      return extension;
    },
    getFileData(file, index) {
      return new Promise((resolve, reject) => {
        const name = this.getFileName(file.name);
        const extension = this.getExtension(file.name);
        if (utils.isImageType(file.type)) {
          // 画像の時の処理
          utils
            .getThumbnail(file, {
              maxHeight: THUMBNAIL_MAX_HEIGHT,
              maxWidth: THUMBNAIL_MAX_WIDTH
            })
            .then(thumbnail => {
              const data = {
                extension: extension,
                file: file,
                index: index,
                name: name,
                thumbnail: thumbnail
              };
              resolve(data);
            })
            .catch(error => {
              reject(error);
            });
        } else {
          // 画像以外の時の処理
          const data = {
            extension: extension,
            file: file,
            index: index,
            name: name
          };
          resolve(data);
        }
      });
    },
    getFileName(name) {
      let names = name.split('.');
      names.pop();
      return names.join('.');
    },
    openModal() {
      if (!this.currentFileData) {
        return;
      }
      this.fileName = this.currentFileData.name;
      this.message = '';
      this.isOpenModal = true;
      this.$nextTick(() => {
        this.focusTextarea();
      });
    },
    readFiles(files) {
      if (!files.length) {
        return;
      }
      let filesData = [];
      let requests = [];
      for (var i = 0; i < files.length; i++) {
        // ファイルチェック
        const error = this.checkFile(files[i]);
        if (error.length) {
          alert(error.join('\n'));
          return;
        }
        requests.push(
          this.getFileData(files[i], i)
            .then(data => {
              filesData[data.index] = data;
            })
            .catch(() => {
              alert(ERROR_MESSAGE.imageSelect);
            })
        );
      }
      Promise.all(requests).finally(() => {
        // 空のアイテムを削除
        this.filesData = filesData.filter(file => file);
        this.current = 0;
        this.openModal();
      });
    },
    reset() {
      this.message = '';
    },
    showError(error) {
      clearTimeout(this.timeoutId);
      this.error = error;
      // 一定時間経過でエラーメッセージを非表示にする
      this.timeoutId = setTimeout(() => {
        this.error = '';
      }, ERROR_MESSAGE_SHOW_DURATION);
    },
    submit() {
      if (this.isProcessing) {
        return;
      }
      this.isProcessing = true;
      const message = this.message.trim();
      const file = this.currentFileData.file;
      const fileName = this.fileName.trim() + this.currentFileData.extension;
      const thumbnail = this.currentFileData.thumbnail;
      const params = {
        message: message
      };

      if (utils.isImageType(file.type)) {
        // 画像の時の処理
        params.image = {
          imageUrl: '',
          storageUri: '',
          thumbnail: thumbnail
        };
      } else {
        // 画像以外の時の処理
        params.file = {
          fileUrl: '',
          fileType: file.type
        };
      }
      this.$firebase
        .addMessage(this.room.id, params, this.room)
        .then(ref => {
          this.isProcessing = false;
          this.closeModal();
          this.$firebase.putFile(ref, file, fileName);
        })
        .catch(() => {
          this.isProcessing = false;
          this.showError(ERROR_MESSAGE.send);
        });
    }
  }
};
</script>
